import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ClearOutlined, LogoutOutlined, SearchOutlined } from '@mui/icons-material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { iebcApi, IEBCArea, IEBCVoter } from '@services/api/iebc';
import { CoatOfArms, FlagStripe } from '@pages/USAJILILoginPage';
import { MIN_SEARCH, SearchBox } from './SearchBox';
import { USAJILIThemeProvider, USAJILIColorModeToggle } from '@theme/USAJILIThemeProvider';

const COLUMNS: Array<{ key: keyof IEBCVoter; label: string }> = [
  { key: 'national_id', label: 'ID number' },
  { key: 'full_name', label: 'Name' },
  { key: 'father_name', label: "Father's name" },
  { key: 'family_name', label: 'Clan name' },
  { key: 'county', label: 'County' },
  { key: 'district', label: 'Subcounty' },
  { key: 'division', label: 'Division' },
  { key: 'location', label: 'Location' },
  { key: 'sub_location', label: 'Sublocation' },
  { key: 'village', label: 'Village' },
];

const fmt = (n: number) => n.toLocaleString('en-KE');

const areaLabel = (a: IEBCArea) =>
  [a.county, a.district, a.division, a.location].filter(Boolean).join(' › ');

const errorMessage = (e: any) => e?.error?.message || 'Something went wrong. Try again.';

/** Signed in to IEBC in this browser? Otherwise the page goes to its login. */
const hasIEBCSession = () => iebcApi.hasSession();

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('md'));

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [signingOut, setSigningOut] = useState(false);

  const me = useQuery({ queryKey: ['iebc', 'me'], queryFn: iebcApi.me, staleTime: 5 * 60 * 1000 });

  // The table shows a search when the Search button (or Enter) is used,
  // or one person when a suggestion is picked; suggestions appear as you type.
  const [searchError, setSearchError] = useState('');
  const showSearch = (text: string) => {
    const s = text.trim();
    if (s !== '' && s.replace(/\s/g, '').length < MIN_SEARCH) {
      setSearchError(`Type at least ${MIN_SEARCH} characters`);
      return;
    }
    setSearchError('');
    setSearch(s);
    setPage(0);
  };
  const runSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    showSearch(searchInput);
  };

  const hasAreas = (me.data?.areas.length ?? 0) > 0;
  // The list is always the user's own location(s), set when the account
  // was created; the server applies it. Only search narrows it further.
  const voters = useQuery({
    queryKey: ['iebc', 'voters', search, page, pageSize],
    queryFn: () => iebcApi.voters({}, search, page + 1, pageSize),
    enabled: hasAreas,
    placeholderData: keepPreviousData,
  });

  // A session that has ended (signed in on another device, signed out,
  // deactivated): the IEBC client clears the stored session when it can't
  // refresh, so an error with no session left means sign in again.
  const authError = (!!me.error || !!voters.error) && !hasIEBCSession();
  useEffect(() => {
    if (authError) navigate('/iebc/login', { replace: true });
  }, [authError, navigate]);

  const filtered = search !== '';

  const signOut = async () => {
    setSigningOut(true);
    await iebcApi.logout().catch(() => undefined);
    navigate('/iebc/login', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2, md: 3 }, py: 1.25 }}>
          <CoatOfArms size={36} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.2 }}>SDICS · IEBC</Typography>
            <Typography noWrap sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              Unregistered voters · view only
            </Typography>
          </Box>
          {!phone && me.data && (
            <Typography noWrap sx={{ fontSize: '0.85rem', color: 'text.secondary', maxWidth: 240 }}>
              {me.data.full_name}
            </Typography>
          )}
          <USAJILIColorModeToggle />
          <Tooltip title="Sign out">
            <span>
              <IconButton onClick={signOut} disabled={signingOut} aria-label="Sign out">
                {signingOut ? <CircularProgress size={18} /> : <LogoutOutlined />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <FlagStripe height={3} />
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, maxWidth: 1600, mx: 'auto' }}>
        {me.isLoading && <LinearProgress />}
        {me.error && !authError && <Alert severity="error">{errorMessage(me.error)}</Alert>}

        {me.data && !hasAreas && (
          <Alert severity="info">
            No area has been assigned to your account yet. Contact the SDICS administrator.
          </Alert>
        )}

        {me.data && hasAreas && (
          <Stack spacing={2}>
            {/* Summary */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.75 }}>
                    {me.data.areas.length === 1 ? 'Your location' : 'Your locations'}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                    {me.data.areas.map((a) => (
                      <Chip key={areaLabel(a)} size="small" label={areaLabel(a)} />
                    ))}
                  </Stack>
                </Box>
                <Box sx={{ textAlign: { sm: 'right' } }}>
                  <Typography sx={{ fontSize: '1.8rem', fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(me.data.unregistered_total)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>unregistered voters</Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Search */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack component="form" onSubmit={runSearch} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <SearchBox
                  value={searchInput}
                  onChange={(v) => {
                    setSearchInput(v);
                    setSearchError('');
                  }}
                  onPick={(v) => {
                    setSearchInput(v.full_name);
                    showSearch(v.national_id);
                  }}
                  onSearch={showSearch}
                  error={searchError}
                />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SearchOutlined />}
                  sx={{ alignSelf: { sm: 'flex-start' }, whiteSpace: 'nowrap', px: 3, height: 40 }}
                >
                  Search
                </Button>
                {(filtered || searchInput !== '') && (
                  <Button
                    startIcon={<ClearOutlined />}
                    onClick={() => {
                      setSearchInput('');
                      setSearchError('');
                      setSearch('');
                      setPage(0);
                    }}
                    sx={{ alignSelf: { sm: 'flex-start' }, whiteSpace: 'nowrap', height: 40 }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Paper>

            {/* Results */}
            {voters.error && !authError && <Alert severity="error">{errorMessage(voters.error)}</Alert>}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontWeight: 600, flex: 1 }}>
                  {voters.data ? `${fmt(voters.data.count)} ${filtered ? 'matching' : 'unregistered voters'}` : 'Loading…'}
                </Typography>
                {voters.isFetching && <CircularProgress size={16} />}
              </Stack>

              {voters.data && voters.data.results.length === 0 ? (
                <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  No unregistered voters match.
                </Typography>
              ) : (
                // On narrow screens the table scrolls sideways inside its box.
                <TableContainer sx={{ overflowX: 'auto', opacity: voters.isPlaceholderData ? 0.6 : 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {COLUMNS.map((c) => (
                          <TableCell key={c.key} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {c.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {voters.data?.results.map((v) => (
                        <TableRow key={v.national_id} hover>
                          {COLUMNS.map((c) => (
                            <TableCell
                              key={c.key}
                              sx={{ whiteSpace: 'nowrap', ...(c.key === 'national_id' ? { fontFamily: 'monospace' } : {}) }}
                            >
                              {v[c.key] || '—'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {voters.data && voters.data.count > 0 && (
                <TablePagination
                  component="div"
                  count={voters.data.count}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage={phone ? 'Per page' : 'Rows per page'}
                />
              )}
            </Paper>

            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', textAlign: 'center' }}>
              Signed in as {me.data.username}. Every search is logged.
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

/** iebc.sdics.tech: view-only list of unregistered voters in the account's areas. */
export const IEBCPage: React.FC = () => {
  if (!hasIEBCSession()) return <Navigate to="/iebc/login" replace />;
  return (
    <USAJILIThemeProvider>
      <Dashboard />
    </USAJILIThemeProvider>
  );
};

export default IEBCPage;
